import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Tooltip,
  TableSortLabel,
  Typography,
  Button,
} from '@mui/material';
import { EditOutlined, DeleteOutlined, Folder, Warning, SwapHoriz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LOAN_LABELS = { custody: 'En custodia', loan: 'En prestamo', returned: 'Devuelto' };

const ProceedingTable = ({ proceedings, onEdit, onDelete, page, onPageChange, pagination, canView, canEdit, canDelete, sortBy, sortDir, onSortChange }) => {
  const navigate = useNavigate();

  if (!proceedings || proceedings.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Folder sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay expedientes registrados</p>
      </Paper>
    );
  }

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES', { timeZone: 'UTC' });

  const addYears = (date, years) => {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };

  const getRetentionInfo = (proc) => {
    const rl = proc.retentionLine;
    if (!rl || !proc.startDate) return null;
    const now = new Date();
    const transferDate = addYears(proc.startDate, rl.localRetention);
    const eliminationDate = addYears(proc.startDate, rl.localRetention + rl.centralRetention);
    const daysToTransfer = Math.ceil((transferDate - now) / (1000 * 60 * 60 * 24));
    const daysToElimination = rl.dispositionE ? Math.ceil((eliminationDate - now) / (1000 * 60 * 60 * 24)) : null;
    return { transferDate, eliminationDate, daysToTransfer, daysToElimination, dispositionE: rl.dispositionE };
  };

  const SortCell = ({ label, field, sx: extraSx }) => (
    <TableCell onClick={() => onSortChange(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const totalPages = pagination ? Math.ceil((pagination.total || 0) / (pagination.limit || 10)) : 1;
  const from = pagination ? ((page - 1) * (pagination.limit || 10)) + 1 : 1;

  return (
    <Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortCell label="Nombre" field="name" />
                <SortCell label="Codigo" field="code" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <SortCell label="Tabla de Retencion" field="retentionLine.retention.name" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                <SortCell label="Fecha Inicial" field="startDate" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>Retencion</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Estado</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proceedings.map((proceeding) => (
                <TableRow
                  key={proceeding.id}
                  hover
                  onClick={() => navigate(`/proceedings/${proceeding.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                      {proceeding.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{proceeding.code}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {proceeding.retentionLine ? (
                      <Typography variant="body2">{proceeding.retentionLine.series}</Typography>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">{formatDate(proceeding.startDate)}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {(() => {
                      const info = getRetentionInfo(proceeding);
                      if (!info) return <Typography variant="caption" color="text.disabled">&mdash;</Typography>;
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Tooltip title={`Trasladar: ${formatDate(info.transferDate)} (${info.daysToTransfer} dias)`}>
                            <Chip
                              icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                              label={`${info.daysToTransfer}d`}
                              size="small"
                              color={info.daysToTransfer <= 0 ? 'error' : info.daysToTransfer <= 90 ? 'warning' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </Tooltip>
                          {info.dispositionE && (
                            <Tooltip title={`Eliminar: ${formatDate(info.eliminationDate)} (${info.daysToElimination} dias)`}>
                              <Chip
                                icon={<Warning sx={{ fontSize: 14 }} />}
                                label={`${info.daysToElimination}d`}
                                size="small"
                                color={info.daysToElimination <= 0 ? 'error' : info.daysToElimination <= 90 ? 'error' : 'default'}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={LOAN_LABELS[proceeding.loan || 'custody'] || proceeding.loan}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: 'rgba(29,31,32,0.16)', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      {canEdit && (
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(proceeding); }} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                            <EditOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(proceeding.id); }} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                            <DeleteOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {pagination && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
            Mostrando {from} de {pagination.total || 0} expedientes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => onPageChange(page - 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
              Anterior
            </Button>
            <Button variant="outlined" size="small" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
              Siguiente
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProceedingTable;
